'use client'

import { Accordion } from 'app/components/atoms'
import { useTranslation } from 'app/i18n/client'
import useQueryCancellationPolicies from 'app/hooks/useQueryCancellationPolicies'

import { Trans } from 'react-i18next'

export type BookingPolicySection = 'cancellation' | 'cleaning' | 'clause'

interface BookingPoliciesProps {
  sections?: BookingPolicySection[]
  defaultClosedSections?: BookingPolicySection[]
  isResale?: boolean
  title?: string
  className?: string
  hotelCode?: string
  compact?: boolean
  roomPlanCodes?: string[]
}

const DEFAULT_SECTIONS: BookingPolicySection[] = [
  'cancellation',
  'cleaning',
  'clause',
]

const BookingPolicies: React.FC<BookingPoliciesProps> = ({
  sections = DEFAULT_SECTIONS,
  defaultClosedSections = [],
  isResale = false,
  title,
  className,
  hotelCode,
  compact = false,
  roomPlanCodes,
}) => {
  const { t } = useTranslation()
  const contentTextClass = compact ? 'text-base' : 'text-lg'

  const { data: cancellationData, isLoading: isCancellationLoading } =
    useQueryCancellationPolicies(
      isResale ? undefined : hotelCode,
      isResale ? undefined : roomPlanCodes,
    )

  const dynamicParagraphs = roomPlanCodes?.length
    ? (cancellationData?.data ?? [])
        .map((entry) => entry.slice(1))
        .flat()
        .filter(Boolean)
    : (cancellationData?.data ?? []).flat().filter(Boolean)

  const cancellationTitle = isResale
    ? t('Resale Service Cancellation Policy Title')
    : t('Cancellation Policy')

  const cancellationStaticContent = isResale ? (
    <div className="text-base">
      <ul className="pl-8 space-y-1 list-disc text-justify">
        <li>{t('Resale Policy Date Change')}</li>
        <li>{t('Resale Policy Number of Guests Change')}</li>
        <li>{t('Resale Policy Regarding Refunds')}</li>
        <li>{t('Resale Policy Regarding Transfer or Resale')}</li>
      </ul>
    </div>
  ) : (
    <div className={contentTextClass}>
      <Trans i18nKey="Cancellation Policy Detail">
        <p>Our cancellation policy is from 2 days before.</p>
        <p>We accept cancellation fee as follows.</p>
        <p>Please refer to details as follows before you reserve.</p>
        <p>[No-show/Same-day] 100% [1 day before] 80% [2 days before] 50%</p>
        <p>
          *As more than 15 people of group, cancellation charge will arise from
          20 days before arrival day.
        </p>
        <p>
          [No-show/Same-day] 100% [1 day before] 80% [2 days before] 50% [10
          days before] 20% [20 days before] 10%
        </p>
        <p>*For some plans, cancellation policy may vary depending on plans.</p>
      </Trans>
    </div>
  )

  const isShowingSkeleton = !!(!isResale && hotelCode && isCancellationLoading)
  const cancellationContent = (
    <div aria-live="polite" aria-busy={isShowingSkeleton}>
      {isResale ? (
        cancellationStaticContent
      ) : isShowingSkeleton ? (
        <div className={`${contentTextClass} space-y-2`}>
          <div className="h-4 bg-gray-150 rounded animate-pulse w-full" />
          <div className="h-4 bg-gray-150 rounded animate-pulse w-11/12" />
          <div className="h-4 bg-gray-150 rounded animate-pulse w-3/4" />
        </div>
      ) : dynamicParagraphs.length > 0 ? (
        <div className={contentTextClass}>
          {dynamicParagraphs.map((paragraph, idx) => (
            <p key={idx}>{paragraph}</p>
          ))}
          <br />
          <p>{t('Group Reservation Cancellation Note')}</p>
        </div>
      ) : (
        cancellationStaticContent
      )}
    </div>
  )

  const cancellation = {
    title: cancellationTitle,
    content: cancellationContent,
  }

  const cleaning = {
    title: t('Room Cleaning'),
    content: (
      <div className={contentTextClass}>
        <p>
          {t(
            'Tokyu Stay has adopted an environmentally friendly cleaning system.',
          )}
        </p>
        <p className="mb-2">
          {t(
            'Please read the following information carefully and make sure you fully understand it.',
          )}
        </p>
        <p>
          {t(
            'For consecutive nights, cleaning will be carried out on a simple cleaning basis on request.',
          )}
        </p>
        <p>
          {t(
            'If you wish to have your room cleaned, please place a sign in front of the door.',
          )}
        </p>
        <p className="mb-2">
          {t(
            'In addition to the above simple cleaning, room cleaning will be performed once a week for guests staying 7 nights or more in a row.',
          )}
        </p>
        <p className="font-bold">&lt;{t('Contents of cleaning')}&gt;</p>
        <p>
          {t(
            '・[Simple cleaning] - Changing towels and nightwear, changing toothpaste sets, collecting trash in trash cans',
          )}
        </p>
        <p>
          {t(
            '・[Room cleaning] - Making beds, changing towels and nightwear, replenishing room supplies, cleaning the room, collecting trash in trash cans.',
          )}
        </p>
      </div>
    ),
  }

  const clause = {
    title: t('Clause'),
    content: (
      <a
        className={contentTextClass}
        href={t('CLAUSE')}
        target="_blank"
        rel="noreferrer"
      >
        {t('CLAUSE')}
      </a>
    ),
  }

  const sectionMap: Record<
    BookingPolicySection,
    { title: string; content: React.ReactNode }
  > = {
    cancellation,
    cleaning,
    clause,
  }

  return (
    <div className={className}>
      {title && (
        <h2 className="mb-8 lg:mb-3 text-2xl font-lora text-center lg:text-left">
          {title}
        </h2>
      )}
      {sections.map((sectionKey) => {
        const item = sectionMap[sectionKey]
        return (
          <div className="mt-5 border border-gray-150" key={sectionKey}>
            <Accordion
              title={item.title}
              defaultOpen={!defaultClosedSections.includes(sectionKey)}
            >
              {item.content}
            </Accordion>
          </div>
        )
      })}
    </div>
  )
}

export default BookingPolicies
